import { useBulkViewFilters } from '@/components/bulkQueue/BulkTableContext';
import { UseSetSearchParamReturn, useSetSearchParam } from '@/hooks/router';
import { AllVisitsContext } from '@/pages/AllVisitsPage/AllVisits.context';
import { ALL_VISIT_COUNTS } from '@/pages/AllVisitsPage/AllVisits.gql';
import {
    AllVisitCountsQuery,
    AllVisitCountsQueryVariables,
} from '@/pages/AllVisitsPage/AllVisits.gql.gql.types';
import { DEFAULT_TAB, TAB_CONFIGS } from '@/pages/AllVisitsPage/AllVisitsPage.constants';
import { AllVisitsPageNS } from '@/pages/AllVisitsPage/AllVisitsPage.types';
import {
    getDefaultSelectedSecondaryTabs,
    parseSecondaryTabsFromUrl,
} from '@/pages/AllVisitsPage/AllVisitsPage.utils';
import { VIEW_FILTERS } from '@/pages/VisitRequestsListPage/queues/VisitRequestQueueTabs';
import { FeatureFlag } from '@/types/featureFlag';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { useQuery } from '@apollo/client';
import { VisitFilter, VisitRequestFilter, VisitRequestWorkflowStep, VisitState } from 'gql-types';
import {
    Dispatch,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react';

export const usePrimaryTabFromSearchParam =
    (): UseSetSearchParamReturn<AllVisitsPageNS.PrimaryTab> => {
        const [selectedTab, setSelectedTab] =
            useSetSearchParam<AllVisitsPageNS.PrimaryTab>('status');

        return [selectedTab, setSelectedTab];
    };

type UsePrimaryTab = () => [
    tab: AllVisitsPageNS.TabConfig,
    setTab: Dispatch<AllVisitsPageNS.TabConfig>,
];

export const usePrimaryTab: UsePrimaryTab = () => {
    const [selectedTab, setSelectedTab] = usePrimaryTabFromSearchParam();

    const tab = useMemo(() => {
        if (selectedTab !== null) {
            const found = TAB_CONFIGS.find((tab) => tab.tab === selectedTab);
            if (!found) {
                setSelectedTab(null);
                return DEFAULT_TAB;
            }

            return found;
        }

        return DEFAULT_TAB;
    }, [selectedTab, setSelectedTab]);

    const setTab = useCallback(
        (nextTab: AllVisitsPageNS.TabConfig) => {
            setSelectedTab(nextTab.tab);
        },
        [setSelectedTab],
    );

    return [tab, setTab];
};

type UseCurrentSecondaryTabs = () => [
    tabs: VisitRequestWorkflowStep[],
    setTabs: Dispatch<SetStateAction<VisitRequestWorkflowStep[]>>,
];

export const useCurrentSecondaryTabs: UseCurrentSecondaryTabs = () => {
    const [primaryTab] = usePrimaryTab();
    const [secondaryParam, setSecondaryParam] = useSetSearchParam('filters');

    const defaultTabs = useMemo(
        () => getDefaultSelectedSecondaryTabs(primaryTab.lowerTabs),
        [primaryTab.lowerTabs],
    );
    const searchParamsSecondaryTabs = useMemo(
        () => parseSecondaryTabsFromUrl(secondaryParam),
        [secondaryParam],
    );

    const validSearchParamTabs = useMemo(() => {
        return searchParamsSecondaryTabs.filter((tab) => {
            if (!primaryTab.lowerTabs) {
                return false;
            }
            return Array.from(primaryTab.lowerTabs).some(([tabTitle]) => tabTitle === tab);
        });
    }, [primaryTab.lowerTabs, searchParamsSecondaryTabs]);

    const selectedTabs = useMemo(() => {
        if (!secondaryParam) {
            return defaultTabs;
        }
        return validSearchParamTabs.length > 0 ? validSearchParamTabs : defaultTabs;
    }, [secondaryParam, validSearchParamTabs, defaultTabs]);

    const setSecondaryParamRef = useRef(setSecondaryParam);
    setSecondaryParamRef.current = setSecondaryParam;

    useEffect(() => {
        if (!secondaryParam) return;
        if (validSearchParamTabs.length !== searchParamsSecondaryTabs.length) {
            const encoded =
                validSearchParamTabs.length > 0
                    ? validSearchParamTabs.map(encodeURIComponent).join(',')
                    : null;
            setSecondaryParamRef.current(encoded, true);
        }
    }, [secondaryParam, validSearchParamTabs, searchParamsSecondaryTabs.length]);

    const setTabs = useCallback(
        (param: SetStateAction<VisitRequestWorkflowStep[]>) => {
            const currentTabs = parseSecondaryTabsFromUrl(secondaryParam);
            const newTabs = typeof param === 'function' ? param(currentTabs) : param;

            const encoded = newTabs.length > 0 ? newTabs.map(encodeURIComponent).join(',') : null;
            setSecondaryParam(encoded, true);
        },
        [secondaryParam, setSecondaryParam],
    );

    useEffect(() => {
        if (!primaryTab.lowerTabs) {
            setSecondaryParam(null, true);
        }
    }, [primaryTab.lowerTabs, setSecondaryParam]);

    return [selectedTabs, setTabs];
};

const getTotalCount = (param?: { totalCount: number | null } | null): number =>
    typeof param?.totalCount === 'number' ? param.totalCount : 0;

const toFilter = <T extends VisitRequestFilter | VisitFilter>(
    filter: T | 'exclude',
    additional: T,
): T => ({
    ...(filter === 'exclude' ? {} : filter),
    ...additional,
});

export const useCounts = () => {
    const {
        dateRange: [[startDate, endDate]],
    } = useContext(AllVisitsContext);
    const isBulkEditVisits = useFeatureFlagEnabled(
        FeatureFlag.DASHBOARD_INCLUDE_VISITS_IN_BULK_QUEUES,
    );

    const filters = useBulkViewFilters({
        startDate,
        endDate,
    });

    const variables = useMemo(
        (): AllVisitCountsQueryVariables => ({
            needsServices: toFilter(
                filters.visitRequestFilters,
                VIEW_FILTERS.NEEDS_SERVICES.visitRequest,
            ),
            needsClinician: toFilter(
                filters.visitRequestFilters,
                VIEW_FILTERS.NEEDS_CLINICIAN.visitRequest,
            ),
            needsDate: toFilter(
                filters.visitRequestFilters,
                VIEW_FILTERS.NEEDS_TENTATIVE_DATE.visitRequest,
            ),
            ready: toFilter(
                filters.visitRequestFilters,
                VIEW_FILTERS.READY_FOR_PROMOTION.visitRequest,
            ),
            needsServicesVisits: toFilter(filters.visitFilters, VIEW_FILTERS.NEEDS_SERVICES.visit),
            needsClinicianVisits: toFilter(
                filters.visitFilters,
                VIEW_FILTERS.NEEDS_CLINICIAN.visit,
            ),
            needsDateVisits: toFilter(
                filters.visitFilters,
                VIEW_FILTERS.NEEDS_TENTATIVE_DATE.visit,
            ),
            unscheduled: toFilter(filters.visitFilters, VIEW_FILTERS.UNSCHEDULED.visit),
            scheduled: toFilter(filters.visitFilters, VIEW_FILTERS.SCHEDULED.visit),
            confirmed: toFilter(filters.visitFilters, VIEW_FILTERS.CONFIRMED.visit),
            enRoute: toFilter(filters.visitFilters, VIEW_FILTERS.EN_ROUTE.visit),
            atLocation: toFilter(filters.visitFilters, VIEW_FILTERS.AT_LOCATION.visit),
            inReview: toFilter(filters.visitFilters, VIEW_FILTERS.IN_REVIEW.visit),
            ended: toFilter(filters.visitFilters, VIEW_FILTERS.ENDED.visit),
            isBulkEditVisits,
        }),
        [filters.visitFilters, filters.visitRequestFilters, isBulkEditVisits],
    );

    const { data } = useQuery<AllVisitCountsQuery, AllVisitCountsQueryVariables>(ALL_VISIT_COUNTS, {
        variables,
        fetchPolicy: 'cache-and-network',
    });

    return useMemo(
        (): Record<AllVisitsPageNS.PrimaryTab, number | null> => ({
            [VisitState.UNSCHEDULED]: getTotalCount(data?.unscheduled),
            [VisitState.SCHEDULED]: getTotalCount(data?.scheduled),
            [VisitState.EN_ROUTE]: getTotalCount(data?.enRoute),
            [VisitState.IN_REVIEW]: getTotalCount(data?.inReview),
            [VisitState.ENDED]: getTotalCount(data?.ended),
            [VisitState.AT_LOCATION]: getTotalCount(data?.atLocation),
            [VisitState.CONFIRMED]: getTotalCount(data?.confirmed),
            [VisitRequestWorkflowStep.READY_FOR_PROMOTION]: getTotalCount(data?.ready),
            [VisitRequestWorkflowStep.NEEDS_SERVICES]:
                getTotalCount(data?.needsServices) + getTotalCount(data?.needsServicesVisits),
            [VisitRequestWorkflowStep.NEEDS_TENTATIVE_DATE]:
                getTotalCount(data?.needsDate) + getTotalCount(data?.needsDateVisits),
            [VisitRequestWorkflowStep.NEEDS_CLINICIAN]:
                getTotalCount(data?.needsClinician) + getTotalCount(data?.needsClinicianVisits),
        }),
        [data],
    );
};
